'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import { useMutation } from '@tanstack/react-query'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { CheckIcon } from 'primitives/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { IconCheck } from '@repo/design-system-cf/components/icons'

import { useFollowUserMutation, useUnfollowUserMutation } from '@web/gen/flow'

export const FollowButton = ({
  userId,
  following: initialFollowing,
  size = 'regular',
  variant = 'primary',
  className,
  isShowUnfollowWithoutIcon = false,
  onFollowSuccess,
}: {
  userId: string
  following?: boolean
  size?: 'regular' | 'small'
  variant?: 'primary' | 'outlined'
  className?: string
  isShowUnfollowWithoutIcon?: boolean
  onFollowSuccess?: () => void
}) => {
  const t = useTranslations('pages.clubhouse.follow_button')
  const { openModal } = useUserAuthModal()
  const { data: isLoggedIn } = useIsLoggedInQuery()

  const [isHovered, setIsHovered] = React.useState(false)
  const [isFollowing, setIsFollowing] = React.useState(initialFollowing)

  const followMutation = useFollowUserMutation()
  const unfollowMutation = useUnfollowUserMutation()
  const { mutate: toggleFollow, isPending: isLoading } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async () => {
      return isFollowing
        ? unfollowMutation.mutateAsync({ userID: userId })
        : followMutation.mutateAsync({ userID: userId })
    },
    onSuccess: () => {
      setIsFollowing(prev => !prev)
      onFollowSuccess?.()
    },
    onError: () => {
      setIsFollowing(prev => !prev)
    },
  })

  function handleToggleFollow() {
    if (!isLoggedIn) {
      openModal('register')
      return
    }
    toggleFollow()
  }

  React.useEffect(() => {
    setIsFollowing(initialFollowing)
  }, [initialFollowing])

  return isFollowing ? (
    <Button
      variant="tertiary"
      size={size}
      className={tw(className)}
      onClick={handleToggleFollow}
      // oxlint-disable-next-line no-negated-condition
      iconLeft={!isShowUnfollowWithoutIcon ? <IconCheck /> : null}
      data-testid="clubhouses-member-following-button"
      loading={isLoading}
    >
      {!isShowUnfollowWithoutIcon ? t('following_button_text') : t('unfollow_text')}
    </Button>
  ) : (
    <Button
      onClick={handleToggleFollow}
      className={tw(className)}
      variant={isFollowing || variant === 'outlined' ? 'secondary' : 'primary'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isLoading}
      size={size}
      data-testid={
        isFollowing ? 'clubhouses-member-following-button' : 'clubhouses-member-follow-button'
      }
    >
      {isFollowing ? (
        <div className="flex flex-row items-center justify-center gap-1.5">
          {isHovered ? (
            t('unfollow_button_text')
          ) : (
            <>
              <CheckIcon />
              <span>{t('followed_button_label')}</span>
            </>
          )}
        </div>
      ) : (
        t('follow_button_text')
      )}
    </Button>
  )
}
